import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import type { Door, Tier } from "../types";
import { opLink } from "../hooks/use-phone-login";

/** missionbank Level strings, keyed by the tier the difficulty screen uses. */
const LEVEL_FOR_TIER: Record<Tier, string> = {
  beginner: "BEGINNER",
  challenging: "CHALLENGING",
  improbable: "MISSION IMPROBABLE",
};

interface DoorSpec {
  family: Door;
  /** The vague description shown ON the door — the whole intrigue mechanic. */
  label: string;
  /** The door's art (missionbank.DoorImage); '' = the hallway's plain door. */
  image?: string;
  /** LEVEL -> MissionID for the concept behind this door. */
  missionsByLevel: Record<string, number>;
}

export interface DailyDoors {
  day: string;
  doors: DoorSpec[];
  /** Tomorrow's two doors, titles only, when the server sends them (the
   *  Comeback Loop seals one before they leave). Absent on older servers. */
  next?: { family: Door; label: string }[];
}

export const doorKeys = { today: ["secretmissions", "doors"] as const };

/** The texted link's day (&d=, when it went out), read on load (the stepper
 *  then takes it off the address): its doors are the ones its preview showed. */
const LINK_DAY = new URLSearchParams(window.location.search).get("d");

/**
 * Today's two doors. The pick is derived server-side from (member, date), so
 * this is stable across a refresh and rotates daily — no client state to keep
 * in sync, and stepper resolves the same mission later.
 */
export const useDailyDoors = () => {
  // the texted link's member, so these are the doors its preview showed (and
  // a cached answer for another member or day is not reused)
  const op = opLink();
  return useQuery({
    queryKey: [...doorKeys.today, op, LINK_DAY],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.mission.doors, {
        params: { ...(op && { op }), ...(LINK_DAY && { d: LINK_DAY }) },
      });
      return (res.data?.result ?? res.data) as DailyDoors;
    },
    // Revisit the server on each mount so the exact offer is recorded for this visit.
    staleTime: 0,
    refetchOnMount: "always",
  });
};

/**
 * The one mission a door + difficulty resolves to.
 *
 * Returned rather than re-derived downstream: the door label promises a
 * specific mission, and anything that works it out a second time can disagree
 * with what the member was shown.
 *
 * The server fills every level behind a door with its own mission (see
 * daily_doors' _fill_levels). A level it could not fill stays null — the file
 * shows as empty rather than repeating another file's mission.
 */
export const missionIdFor = (
  doors: DailyDoors | undefined,
  door: Door | null,
  tier: Tier | null,
): number | null => {
  if (!doors || !door || !tier) return null;
  const spec = doors.doors.find((d) => d.family === door);
  return spec?.missionsByLevel[LEVEL_FOR_TIER[tier]] ?? null;
};
